import { useMemo, useState } from "react"
import type { FilterFn, SortingFn } from "@tanstack/react-table"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Separator from "@/v2-ui/separator"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { buildCustomerActivityLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import UsageScoreBadge from "@/v2-backoffice-client/usage/UsageScoreBadge"
import UsageSparkline from "@/v2-backoffice-client/usage/UsageSparkline"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import LicenseTrialClock from "@/v2-backoffice-client/license/trials/LicenseTrialClock"
import LicenseTrialMembersTable from "@/v2-backoffice-client/license/trials/LicenseTrialMembersTable"
import {
  TRIAL_CLOCK_LABELS,
  TRIAL_NEW_FILTER_VALUE,
  TRIAL_PULSE_DESCRIPTIONS,
  TRIAL_PULSE_LABELS,
  TRIAL_PULSE_ORDER,
  TRIAL_PULSE_VISUALS,
  TRIALS_PILL_VALUES,
  TRIALS_SEARCH_PARAM,
  TRIALS_STATUS_PARAM,
  type TrialClockState,
  type TrialPulse,
} from "@/v2-backoffice-client/license/trials/license.trials.constants"
import type { TrialCustomer } from "@/v2-backoffice-client/license/trials/license.trials.utils"

export type LicenseTrialTableProps = Omit<
  TableProps<TrialCustomer>,
  "data" | "columns"
> & {
  customers: TrialCustomer[]
  isLoading?: boolean
  error?: Error | null
  onRefresh?(): void
}

// The pills filter the visible Status and Trial clock columns themselves
// (their cell value is the bucket key; sorting is by severity / days left
// through a sortingFn), so the columns panel and the group-by menu list each
// idea once. "New this week" has no column of its own and rides a virtual
// carrier.
const PULSE_COLUMN = "pulse"
const CLOCK_COLUMN = "clock"
const IS_NEW_COLUMN = "isNew"
const SEARCH_COLUMN = "name"

const COLUMN_SIZING = {
  name: 220,
  owner: 150,
  products: 200,
  [CLOCK_COLUMN]: 190,
  [PULSE_COLUMN]: 130,
  people: 120,
  active7d: 100,
  active28d: 100,
  activity: 120,
  trend: 140,
  score: 120,
  lastActive: 120,
}

/** A multi-select over a list column matches when ANY of the row's values is picked. */
const anyOfFilterFn: FilterFn<TrialCustomer> = (row, columnId, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true
  const picked = new Set(
    filterValue.map((value) => String(value).toLowerCase()),
  )
  const values = row.getValue<string[]>(columnId)
  return values.some((value) => picked.has(value.toLowerCase()))
}

/** Soonest-deciding clock first; a trial without an end date sorts last. */
function clockSortKey(customer: TrialCustomer) {
  return customer.clock?.facts.daysLeft ?? Number.MAX_SAFE_INTEGER
}

const sortByClock: SortingFn<TrialCustomer> = (rowA, rowB) =>
  clockSortKey(rowA.original) - clockSortKey(rowB.original)

/** Engaged first, then cooling, silent, no users — the severity order. */
const sortByPulse: SortingFn<TrialCustomer> = (rowA, rowB) =>
  TRIAL_PULSE_ORDER.indexOf(rowA.original.pulse) -
  TRIAL_PULSE_ORDER.indexOf(rowB.original.pulse)

function share(part: number, total: number) {
  return total > 0 ? ` · ${Math.round((part / total) * 100)} %` : ""
}

type BuildColumnsInput = {
  ownerOptions: { label: string; value: string }[]
  productOptions: { label: string; value: string }[]
}

// Built per render set (not a module constant) because the owner and
// product facets list what is actually on trial right now.
function buildColumns(input: BuildColumnsInput) {
  const { ownerOptions, productOptions } = input
  return createColumns<TrialCustomer>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      name: {
        id: "name",
        label: "Customer",
        getValue: (row) => row.name,
        renderCell({ row }) {
          const { externalCustomerId, name } = row.original
          if (!externalCustomerId) return <span>{name}</span>
          return (
            <Link
              to={buildCustomerActivityLink(externalCustomerId)}
              iconSrc={null}
              className="font-medium"
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              {name}
            </Link>
          )
        },
        exportValue: ({ row }) => row.original.name,
      },
      // The "New this week" pill's carrier: no cell of its own, the clock
      // column's start date says when.
      [IS_NEW_COLUMN]: {
        id: IS_NEW_COLUMN,
        label: "New this week",
        virtual: true,
        enableSorting: false,
        getValue: (row) => (row.isNew ? TRIAL_NEW_FILTER_VALUE : ""),
        exportValue: ({ row }) => (row.original.isNew ? "yes" : "no"),
      },
      owner: {
        id: "owner",
        label: "Owner",
        description:
          "The HubSpot company owner — whose book the trial is in. Group by it for one list per seller.",
        enableGrouping: true,
        getValue: (row) => row.owner ?? "",
        renderGroupLabel: (value) => (value ? String(value) : "No owner"),
        filter: {
          type: "multi-select",
          placeholder: "Any owner",
          toolbar: true,
          options: ownerOptions,
        },
        renderCell({ row }) {
          return row.original.owner ?? null
        },
        exportValue: ({ row }) => row.original.owner ?? "",
      },
      products: {
        id: "products",
        label: "On trial",
        description:
          "The products on trial, from the license domain — an MCP pilot counts, even where the customers board's status does not show it.",
        enableSorting: false,
        getValue: (row) => row.products,
        filter: {
          type: "multi-select",
          placeholder: "Any product",
          toolbar: true,
          options: productOptions,
        },
        filterFn: anyOfFilterFn,
        renderCell({ row }) {
          const { licenses } = row.original
          return (
            <span className="license-trial-products">
              {licenses.map((license) => {
                const label = license.planName
                  ? `${license.productName} · ${license.planName}`
                  : license.productName
                const title = [
                  label,
                  license.seats != null
                    ? `${license.seats} ${license.seats === 1 ? "seat" : "seats"}`
                    : null,
                  license.seatsAreAssigned
                    ? `${license.seatsAssigned} named`
                    : null,
                  license.endsAt
                    ? `ends ${formatDateString(license.endsAt)}`
                    : "no end date",
                ]
                  .filter(Boolean)
                  .join(" · ")
                return (
                  <Badge
                    key={license.id}
                    size="tiny"
                    variant="outline"
                    title={title}
                  >
                    {license.productName}
                  </Badge>
                )
              })}
            </span>
          )
        },
        exportValue: ({ row }) => row.original.products.join(", "),
      },
      // Cell value = the clock state (what the Ending soon / Overdue pills
      // and grouping read); the sort is by days left.
      [CLOCK_COLUMN]: {
        id: CLOCK_COLUMN,
        label: "Trial clock",
        description:
          "Days left on the soonest-ending trial license, with the elapsed share of the trial as a bar. Red once the end date has passed with the trial still open.",
        enableGrouping: true,
        getValue: (row) => row.clockState,
        sortingFn: sortByClock,
        renderGroupLabel: (value) =>
          TRIAL_CLOCK_LABELS[value as TrialClockState] ?? String(value),
        renderCell({ row }) {
          return (
            <LicenseTrialClock
              clock={row.original.clock}
              state={row.original.clockState}
              startedAt={row.original.startedAt}
            />
          )
        },
        exportValue: ({ row }) =>
          row.original.clock
            ? `${formatDateString(row.original.clock.endsAt)} (${row.original.clock.facts.label})`
            : TRIAL_CLOCK_LABELS[row.original.clockState],
      },
      // Cell value = the pulse key (what the status pills and grouping
      // read); the sort is by severity.
      [PULSE_COLUMN]: {
        id: PULSE_COLUMN,
        label: "Status",
        enableGrouping: true,
        getValue: (row) => row.pulse,
        sortingFn: sortByPulse,
        renderGroupLabel: (value) =>
          TRIAL_PULSE_LABELS[value as TrialPulse] ?? String(value),
        renderCell({ row }) {
          const { pulse } = row.original
          const visual = TRIAL_PULSE_VISUALS[pulse]
          return (
            <Badge
              size="xs"
              variant={visual.variant}
              iconSrc={visual.iconSrc}
              title={TRIAL_PULSE_DESCRIPTIONS[pulse]}
            >
              {TRIAL_PULSE_LABELS[pulse]}
            </Badge>
          )
        },
        exportValue: ({ row }) => TRIAL_PULSE_LABELS[row.original.pulse],
      },
      people: {
        id: "people",
        label: "People",
        description:
          "Users on the customer, against the trial's seat cap where it has one. Click the row for the people themselves.",
        getValue: (row) => row.summary.seatCount,
        renderCell({ row }) {
          const { summary, licensedSeats } = row.original
          return (
            <span className="inline-flex items-center gap-1 tabular-nums">
              {formatNumber(summary.seatCount)}
              {licensedSeats != null && (
                <span className="text-muted-foreground">
                  / {formatNumber(licensedSeats)}
                </span>
              )}
              <Icon
                src="angle-small-right"
                size={14}
                className="license-trial-expand-icon text-muted-foreground"
              />
            </span>
          )
        },
        exportValue: ({ row }) => row.original.summary.seatCount,
        summaryValue: ({ row }) => row.original.summary.seatCount,
      },
      active7d: {
        id: "active7d",
        label: "Active · 7 d",
        getValue: (row) => row.summary.activeUsers7d,
        renderCell({ row }) {
          const { activeUsers7d, seatCount } = row.original.summary
          return (
            <span
              className={cn(
                "tabular-nums",
                activeUsers7d === 0 && "text-muted-foreground",
              )}
            >
              {formatNumber(activeUsers7d)}
              <span className="text-muted-foreground">
                {share(activeUsers7d, seatCount)}
              </span>
            </span>
          )
        },
        exportValue: ({ row }) => row.original.summary.activeUsers7d,
        summaryValue: ({ row }) => row.original.summary.activeUsers7d,
      },
      active28d: {
        id: "active28d",
        label: "Active · 28 d",
        getValue: (row) => row.summary.activeUsers28d,
        renderCell({ row }) {
          const { activeUsers28d, seatCount } = row.original.summary
          return (
            <span
              className={cn(
                "tabular-nums",
                activeUsers28d === 0 && "text-muted-foreground",
              )}
            >
              {formatNumber(activeUsers28d)}
              <span className="text-muted-foreground">
                {share(activeUsers28d, seatCount)}
              </span>
            </span>
          )
        },
        exportValue: ({ row }) => row.original.summary.activeUsers28d,
        summaryValue: ({ row }) => row.original.summary.activeUsers28d,
      },
      activity: {
        id: "activity",
        label: "Activity · 28 d",
        description:
          "The customer's weighted activity per day over the last 28 days, every person summed.",
        getValue: (row) =>
          row.summary.weightedEvents28d ?? row.summary.events28d,
        renderCell({ row }) {
          const { summary, daily28d } = row.original
          if (summary.activeUsers28d === 0) {
            return <span className="text-muted-foreground">no activity</span>
          }
          return <UsageSparkline series={daily28d} className="h-5 w-24" />
        },
        exportValue: ({ row }) =>
          Math.round(
            row.original.summary.weightedEvents28d ??
              row.original.summary.events28d,
          ),
      },
      trend: {
        id: "trend",
        label: "Trend",
        getValue: (row) => row.summary.trendPct ?? Number.NEGATIVE_INFINITY,
        renderCell({ row }) {
          const { summary } = row.original
          if (summary.events28d === 0 && summary.eventsPrev28d === 0) {
            return null
          }
          return (
            <UsageTrendBadge
              trendPct={summary.trendPct}
              detail={formatTrendDetail(
                summary.events28d,
                summary.eventsPrev28d,
              )}
            />
          )
        },
        exportValue: ({ row }) =>
          row.original.summary.trendPct != null
            ? Math.round(row.original.summary.trendPct)
            : "",
      },
      score: {
        id: "score",
        label: "Score",
        description:
          "Mean engagement score of the active people — how deeply the trial is being used, not just whether.",
        getValue: (row) => row.summary.avgScore ?? -1,
        renderCell({ row }) {
          const { avgScore } = row.original.summary
          if (avgScore == null) return null
          return <UsageScoreBadge score={avgScore} />
        },
        exportValue: ({ row }) => row.original.summary.avgScore ?? "",
      },
      lastActive: {
        id: "lastActive",
        label: "Last active",
        getValue: (row) => row.summary.lastActiveAt ?? "",
        renderCell({ row }) {
          const { lastActiveAt } = row.original.summary
          if (!lastActiveAt) {
            return <span className="text-muted-foreground">never</span>
          }
          return <RelativeDate date={lastActiveAt} />
        },
        exportValue: ({ row }) =>
          formatDateString(row.original.summary.lastActiveAt),
      },
      startedAt: {
        id: "startedAt",
        label: "Started",
        defaultHidden: true,
        getValue: (row) => row.startedAt ?? "",
        renderCell({ row }) {
          return row.original.startedAt
            ? formatDateString(row.original.startedAt)
            : null
        },
        exportValue: ({ row }) => formatDateString(row.original.startedAt),
      },
      endsAt: {
        id: "endsAt",
        label: "Ends",
        defaultHidden: true,
        getValue: (row) => row.clock?.endsAt ?? "",
        renderCell({ row }) {
          return row.original.clock
            ? formatDateString(row.original.clock.endsAt)
            : null
        },
        exportValue: ({ row }) => formatDateString(row.original.clock?.endsAt),
      },
    },
  })
}

// One row per trialing customer: who owns the relationship, what is on
// trial, how long is left, and whether anyone uses it. A click on the row
// opens the people behind it. The pills are the worklists the weekly post
// used to spell out; owner and product are facets; everything exports.
function LicenseTrialTable(props: LicenseTrialTableProps) {
  const { customers, isLoading, error, onRefresh, className, ...tableProps } =
    props

  const [expandedId, setExpandedId] = useState<string | null>(null)

  const ownerOptions = useMemo(
    () =>
      [...new Set(customers.map((customer) => customer.owner ?? ""))]
        .filter((owner) => owner !== "")
        .sort((a, b) => a.localeCompare(b))
        .map((owner) => ({ label: owner, value: owner })),
    [customers],
  )
  const productOptions = useMemo(
    () =>
      [...new Set(customers.flatMap((customer) => customer.products))]
        .sort((a, b) => a.localeCompare(b))
        .map((product) => ({ label: product, value: product })),
    [customers],
  )
  const columns = useMemo(
    () => buildColumns({ ownerOptions, productOptions }),
    [ownerOptions, productOptions],
  )

  const counts = useMemo(() => {
    const byPulse: Record<TrialPulse, number> = {
      engaged: 0,
      cooling: 0,
      silent: 0,
      "no-users": 0,
    }
    let endingSoon = 0
    let overdue = 0
    let fresh = 0
    for (const customer of customers) {
      byPulse[customer.pulse] += 1
      if (customer.clockState === "ending-soon") endingSoon += 1
      if (customer.clockState === "overdue") overdue += 1
      if (customer.isNew) fresh += 1
    }
    return { byPulse, endingSoon, overdue, fresh }
  }, [customers])

  return (
    <Table
      className={cn("license-trial-table", className)}
      tableId="license-trial-table"
      data={customers}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      getRowId={(row) => String(row.customerId)}
      enableGrouping
      enableRowSelection={false}
      expandedRowId={expandedId}
      renderRowDetail={(row) => (
        <LicenseTrialMembersTable customer={row.original} />
      )}
      onRowClick={(row) => {
        setExpandedId((previous) => (previous === row.id ? null : row.id))
      }}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          column={PULSE_COLUMN}
          urlParam={TRIALS_STATUS_PARAM}
          isLoading={isLoading}
        >
          <Table.StatusFilter.Option value={null} count={customers.length}>
            All
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={TRIALS_PILL_VALUES.ENGAGED}
            filterValue="engaged"
            count={counts.byPulse.engaged}
            variant="success"
            title={TRIAL_PULSE_DESCRIPTIONS.engaged}
          >
            {TRIAL_PULSE_LABELS.engaged}
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={TRIALS_PILL_VALUES.COOLING}
            filterValue="cooling"
            count={counts.byPulse.cooling}
            iconSrc={TRIAL_PULSE_VISUALS.cooling.iconSrc}
            variant="warning"
            title={TRIAL_PULSE_DESCRIPTIONS.cooling}
          >
            {TRIAL_PULSE_LABELS.cooling}
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={TRIALS_PILL_VALUES.SILENT}
            filterValue="silent"
            count={counts.byPulse.silent}
            iconSrc={TRIAL_PULSE_VISUALS.silent.iconSrc}
            variant="destructive"
            title={TRIAL_PULSE_DESCRIPTIONS.silent}
          >
            {TRIAL_PULSE_LABELS.silent}
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={TRIALS_PILL_VALUES.NO_USERS}
            filterValue="no-users"
            count={counts.byPulse["no-users"]}
            iconSrc={TRIAL_PULSE_VISUALS["no-users"].iconSrc}
            title={TRIAL_PULSE_DESCRIPTIONS["no-users"]}
          >
            {TRIAL_PULSE_LABELS["no-users"]}
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={TRIALS_PILL_VALUES.ENDING_SOON}
            filterColumn={CLOCK_COLUMN}
            filterValue="ending-soon"
            count={counts.endingSoon}
            iconSrc="hourglass"
            variant="warning"
            title="Trials that end within the next 7 days — convert or end them."
          >
            {TRIAL_CLOCK_LABELS["ending-soon"]}
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={TRIALS_PILL_VALUES.OVERDUE}
            filterColumn={CLOCK_COLUMN}
            filterValue="overdue"
            count={counts.overdue}
            iconSrc="triangle-warning"
            variant="destructive"
            title="Trials past their end date that are still open — they need a decision."
          >
            {TRIAL_CLOCK_LABELS.overdue}
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={TRIALS_PILL_VALUES.NEW}
            filterColumn={IS_NEW_COLUMN}
            filterValue={TRIAL_NEW_FILTER_VALUE}
            count={counts.fresh}
            iconSrc="sparkles"
            title="Trials that started in the last 7 days."
          >
            New this week
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search
          column={SEARCH_COLUMN}
          urlParam={TRIALS_SEARCH_PARAM}
          placeholder="Search by customer…"
        />
        <Table.ToolbarFilters />
        <Table.GroupSelector />
        <Table.ColumnButton />
        <Separator orientation="vertical" />
        <Table.ToolbarMenu onRefresh={onRefresh} exportFileName="trials" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.Content
        countLabel="customers on trial"
        isLoading={isLoading}
        error={error}
        renderEmpty={
          <p className="p-6 text-center text-sm text-muted-foreground">
            No customer is on trial right now.
          </p>
        }
      />
    </Table>
  )
}

export default LicenseTrialTable
